import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { MONTHS_SHORT } from "../../utils/dates";

import DatePicker from "./DatePicker";
import Form from 'react-bootstrap/Form';

import './DateRange.scss';
import ChevronIcon from "../../assets/icons/ChevronIcon";

/* TO FORCE A DATE on the browser tab:

const OriginalDate = Date;
window.Date = function(...args) {
  return args.length ? new OriginalDate(...args) : new OriginalDate('2025-01-01T00:00:00Z');
};
window.Date.now = () => new OriginalDate('2025-01-01T00:00:00Z').getTime();
window.Date.UTC = OriginalDate.UTC;
window.Date.parse = OriginalDate.parse;

*/

export default function DateRange({ from, to, onChangeFrom, onChangeTo, earliestDate, dateRange }) {
  const [month, setMonth] = useState();
  const [year, setYear] = useState();

  const yearsRange = useMemo(() => {
    const earliestYear = DateTime.fromISO(earliestDate).year;
    const latestYear = DateTime.now().year;

    const years = [];
    
    for (let i = earliestYear; i <= latestYear; ++i) {
      years.push(i);
    }

    return years;
  }, [earliestDate]);

  useEffect(() => {
    let date = !dateRange ? DateTime.now() : DateTime.fromISO(dateRange.dates[0].value);

    // only add one month if the next month is available for selection:
    if (date.endOf('month') <= DateTime.now()) {
      date = date.plus({ months: 1 });
    }

    setYear(date.year);
    setMonth(date.month);

  }, [dateRange]);

  const changeFrom = date => {
    onChangeFrom(date);
  }

  const changeTo = date => {
    // setTo(date);
    onChangeTo(date);
  }

  const onChangeDate = date => {
    if (from === null) {
      changeFrom(date);
    }
    else if (to === null) {
      // if the second date is higher, reverse them
      const fromDate = DateTime.fromISO(from);
      const toDate = DateTime.fromISO(date);

      if (fromDate > toDate) {
        changeFrom(date);
        changeTo(from);
      }
      else {
        changeTo(date);
      }

    }
    else {
      changeFrom(date);
      changeTo(null);
    }
  }

  const previousYear = useMemo(() => {
    const date = DateTime.local(year, month);
    return date.minus({ months: 1 }).year;
  }, [year, month]);

  const previousMonth = useMemo(() => {
    const date = DateTime.local(year, month);
    return date.minus({ months: 1 }).month;
  }, [year, month]);

  const onClickShiftMonth = months => {
    const date = DateTime.local(year, month).plus({ months });

    setYear(date.year);
    setMonth(date.month);
  }

  const onChangeMonth = (value, isPrevious) => {
    const month = parseInt(value, 10);
    const date = DateTime.local(isPrevious ? previousYear : year, month).plus({ months: isPrevious ? 1 : 0 });

    setYear(date.year);
    setMonth(date.month);
  }

  const onChangeYear = (value, isPrevious) => {
    const year = parseInt(value, 10);
    const date = DateTime.local(year, isPrevious ? previousMonth : month).plus({ months: isPrevious ? 1 : 0 });

    setYear(date.year);
    setMonth(date.month);
  }

  const monthDisabled = (year, month) => {
    const now = DateTime.now();
    const date = DateTime.local(year, month);
    const earliest = DateTime.fromISO(earliestDate).startOf('month');

    return (date < earliest || date > now.endOf('month'));
  }

  return (
    <div className="__date-range">
      <div className="date-before">
        <div className="range-navigation">
          <button className="month-switch month-previous" onClick={() => onClickShiftMonth(-1)} disabled={DateTime.local(previousYear, previousMonth).minus({ months: 1 }) < DateTime.fromISO(earliestDate).startOf('month')}><ChevronIcon /></button>

          <div className="month-year-select">
            <Form.Select value={previousMonth} onChange={e => onChangeMonth(e.target.value, true)}>
              { MONTHS_SHORT.map((month, index) => <option key={month} value={index + 1} disabled={monthDisabled(previousYear, index + 1)}>{ month }</option>)}
            </Form.Select>

            <Form.Select value={previousYear} onChange={e => onChangeYear(e.target.value, true)}>
              { previousYear === yearsRange[0] - 1 && <option value={yearsRange[0] - 1} disabled>{ yearsRange[0] - 1}</option> }
              { yearsRange.map(year => <option key={year} value={year}>{ year }</option>)}
              { previousYear === yearsRange[yearsRange.length - 1] + 1 && <option value={yearsRange[yearsRange.length - 1] + 1} disabled>{ yearsRange[yearsRange.length - 1] + 1}</option> }
            </Form.Select>
          </div>

          <div className="month-gap" />
        </div>

        <DatePicker year={previousYear} month={previousMonth} to={to} from={from} onChange={onChangeDate} earliestDate={earliestDate} />
      </div>
      <div className="date-after">
        <div className="range-navigation">
          <div className="month-gap" />

          <div className="month-year-select">
            <Form.Select value={month} onChange={e => onChangeMonth(e.target.value)}>
              { MONTHS_SHORT.map((month, index) => <option key={month} value={index + 1} disabled={monthDisabled(year, index + 1)}>{ month }</option>)}
            </Form.Select>

            <Form.Select value={year} onChange={e => onChangeYear(e.target.value)}>
              { year === yearsRange[0] - 1 && <option value={yearsRange[0] - 1} disabled>{ yearsRange[0] - 1}</option> }
              { yearsRange.map(year => <option key={year} value={year}>{ year }</option>)}
              { year === yearsRange[yearsRange.length - 1] + 1 && <option value={yearsRange[yearsRange.length - 1] + 1} disabled>{ yearsRange[yearsRange.length - 1] + 1}</option> }
            </Form.Select>
          </div>

          <button className="month-switch month-after" onClick={() => onClickShiftMonth(1)} disabled={DateTime.now() < DateTime.local(year, month).endOf('month')}><ChevronIcon /></button>
        </div>

        <DatePicker year={year} month={month} to={to} from={from} onChange={onChangeDate} earliestDate={earliestDate} />
      </div>
    </div>
  )
}
